import { useCallback, useState } from "react";

import type { S3ObjectInput } from "@/lib/gql/__generated__/graphql";

import type { DesignSnackFormBaseModelOnSetViewModelType } from "..";

type Props = {
	onSetViewModel: DesignSnackFormBaseModelOnSetViewModelType;
};

export type SnackFormBaseModelMethodsType = {
	onSetTitle: (input: string) => void;
	onSetThumbnail: (input: string) => void;
	onSetDescription: (input: string) => void;
	onSetSlug: (input: string) => void;
	onSetIsTemplate: (input: boolean) => void;
	onSetSnack: (input: S3ObjectInput) => void;

	shouldTransitionBlockStatusMethods: {
		shouldTransitionBlockStatus: boolean;
		activateTransitionBlockStatus: () => void;
		deactivateTransitionBlockStatus: () => void;
	};
};

export const useSnackFormModelMethods = ({ onSetViewModel }: Props): SnackFormBaseModelMethodsType => {
	const [shouldTransitionBlockStatus, setShouldTransitionBlockStatus] = useState(true);
	const activateTransitionBlockStatus = useCallback(() => {
		setShouldTransitionBlockStatus(true);
	}, []);
	const deactivateTransitionBlockStatus = useCallback(() => {
		setShouldTransitionBlockStatus(false);
	}, []);

	const onSetTitle = useCallback(
		(input: string) => {
			activateTransitionBlockStatus();

			onSetViewModel([
				{
					key: "title",
					value: {
						isEdited: true,
						value: input,
					},
				},
			]);
		},
		[activateTransitionBlockStatus, onSetViewModel],
	);

	const onSetThumbnail = useCallback(
		(input: string) => {
			activateTransitionBlockStatus();

			onSetViewModel([
				{
					key: "thumbnail",
					value: {
						isEdited: true,
						value: input,
					},
				},
			]);
		},
		[activateTransitionBlockStatus, onSetViewModel],
	);

	const onSetDescription = useCallback(
		(input: string) => {
			activateTransitionBlockStatus();

			onSetViewModel([
				{
					key: "description",
					value: {
						isEdited: true,
						value: input,
					},
				},
			]);
		},
		[activateTransitionBlockStatus, onSetViewModel],
	);

	const onSetSlug = useCallback(
		(input: string) => {
			activateTransitionBlockStatus();

			onSetViewModel([
				{
					key: "slug",
					value: {
						isEdited: true,
						value: input,
					},
				},
			]);
		},
		[activateTransitionBlockStatus, onSetViewModel],
	);

	const onSetIsTemplate = useCallback(
		(input: boolean) => {
			activateTransitionBlockStatus();

			onSetViewModel([
				{
					key: "isParentTemplate",
					value: {
						isEdited: true,
						value: input,
					},
				},
			]);
		},
		[activateTransitionBlockStatus, onSetViewModel],
	);

	const onSetSnack = useCallback(
		(input: S3ObjectInput) => {
			activateTransitionBlockStatus();

			onSetViewModel([
				{
					key: "snack",
					value: input,
				},
			]);
		},
		[activateTransitionBlockStatus, onSetViewModel],
	);

	return {
		onSetTitle,
		onSetThumbnail,
		onSetDescription,
		onSetSlug,
		onSetIsTemplate,
		onSetSnack,

		shouldTransitionBlockStatusMethods: {
			shouldTransitionBlockStatus,
			activateTransitionBlockStatus,
			deactivateTransitionBlockStatus,
		},
	};
};
